import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { getOwnedServerMetadata } from '@/lib/eggsApi'
import { getPackageSummaries } from '@/lib/packageBillingApi'
import { canObserveServerResources } from '@/lib/server-utils'
import type { Server } from '@/types'
import { ServerCancelledError, type ServerCancellationReceipt } from '@/lib/serverCancellation'

// Query keys for consistent caching
export const serverKeys = {
  all: ['servers'] as const,
  lists: () => [...serverKeys.all, 'list'] as const,
  list: (filters: string) => [...serverKeys.lists(), { filters }] as const,
  details: () => [...serverKeys.all, 'detail'] as const,
  detail: (id: string) => [...serverKeys.details(), id] as const,
  resources: (id: string) => [...serverKeys.detail(id), 'resources'] as const,
}

export function applyAcceptedServerCancellation(queryClient: QueryClient, receipt: ServerCancellationReceipt) {
  // Cancel earlier reads so they cannot restore a server after its accepted cutoff.
  void queryClient.cancelQueries({ queryKey: serverKeys.all })
  const removeCancelled = <T extends { uuid: string }>(servers: T[] | undefined) =>
    servers?.filter(server => server.uuid !== receipt.server_uuid)
  queryClient.setQueriesData<Server[]>({ queryKey: serverKeys.lists() }, removeCancelled)
  queryClient.setQueriesData<Array<{ uuid: string }>>({ queryKey: ['servers', 'search'] }, removeCancelled)
  queryClient.removeQueries({ queryKey: serverKeys.detail(receipt.server_uuid) })
  queryClient.removeQueries({ queryKey: serverKeys.detail(receipt.server_uuid.slice(0, 8)) })
  void queryClient.invalidateQueries({ queryKey: serverKeys.all })
}

// Hook for fetching all servers
export function useServers() {
  return useQuery({
    queryKey: serverKeys.lists(),
    queryFn: () => apiClient.getServers(),
    staleTime: 30_000, // 30 seconds
    // Poll safe metadata, not blocked /resources routes, until access changes.
    refetchInterval: query => (query.state.data || []).some(server => !canObserveServerResources(server)) ? 10_000 : false,
  })
}

// Hook for fetching server created_at dates from Hoard
export function useServerDates() {
  return useQuery({
    queryKey: [...serverKeys.all, 'dates'] as const,
    queryFn: () => apiClient.getServerDates(),
    staleTime: 5 * 60_000, // 5 minutes — dates don't change
  })
}

// Hook for fetching server billing tiers from Hoard
export function useServerTiers() {
  return useQuery({
    queryKey: [...serverKeys.all, 'tiers'] as const,
    queryFn: () => apiClient.getServerTiers(),
    staleTime: 5 * 60_000, // 5 minutes — tiers rarely change
  })
}

// Hook for fetching server details
export function useServer(uuid: string) {
  return useQuery({
    queryKey: serverKeys.detail(uuid),
    queryFn: () => apiClient.getServer(uuid),
    enabled: !!uuid,
    retry: (failureCount, error) => !(error instanceof ServerCancelledError) && failureCount < 3,
    staleTime: 60_000, // 1 minute for individual server details
    // Resource-backed pages return 409 while Panel is provisioning. Poll the
    // safe detail endpoint so those pages can unlock when installation ends;
    // the console WebSocket connects separately to stream installation events.
    refetchInterval: (query) => !(query.state.error instanceof ServerCancelledError) && query.state.data && !canObserveServerResources(query.state.data as Server)
      ? 5_000
      : false,
  })
}

// Authoritative Panel egg IDs and service types for the authenticated user's servers.
export function useServerMetadata() {
  return useQuery({
    queryKey: [...serverKeys.all, 'metadata'] as const,
    queryFn: getOwnedServerMetadata,
    staleTime: 5 * 60_000,
  })
}

// Hook for server resources with shorter cache time for live data
export function useServerResources(uuid: string, enabled = true) {
  return useQuery({
    queryKey: serverKeys.resources(uuid),
    queryFn: () => apiClient.getServerResources(uuid),
    enabled: !!uuid && enabled,
    staleTime: 5_000, // 5 seconds for live resource data
    refetchInterval: 10_000, // Auto-refetch every 10 seconds
  })
}

// Hook for batch server resources
export function useBatchServerResources(serverIds: string[]) {
  const key = [...serverIds].sort().join(',')
  return useQuery({
    queryKey: ['servers', 'resources', 'batch', key],
    queryFn: () => apiClient.getBatchServerResources(serverIds),
    enabled: serverIds.length > 0,
    staleTime: 5_000,
    refetchInterval: 15_000, // Less frequent for batch requests
  })
}

// Billing refreshes independently from live resource telemetry and is shared by
// every server card through one owner-scoped request.
export function usePackageBillingSummaries(enabled = true) {
  return useQuery({
    queryKey: [...serverKeys.all, 'package-billing-summaries'] as const,
    queryFn: getPackageSummaries,
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

// Mutation for server power actions
export function useServerPowerMutation() {
  const queryClient = useQueryClient()
  
  return useMutation<any, Error, { uuid: string; action: 'start' | 'stop' | 'restart' | 'kill' }>({
    mutationFn: ({ uuid, action }: { uuid: string; action: 'start' | 'stop' | 'restart' | 'kill' }) =>
      apiClient.setServerPowerState(uuid, action),
    onMutate: async (variables: { uuid: string; action: 'start' | 'stop' | 'restart' | 'kill' }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: serverKeys.detail(variables.uuid) })
      await queryClient.cancelQueries({ queryKey: serverKeys.resources(variables.uuid) })
      await queryClient.cancelQueries({ queryKey: serverKeys.lists() })
      
      // Snapshot previous values
      const previousServer = queryClient.getQueryData(serverKeys.detail(variables.uuid))
      const previousList = queryClient.getQueryData(serverKeys.lists())
      
      // Optimistically update status based on action
      const optimisticStatus = 
        variables.action === 'start' ? 'starting' :
        variables.action === 'stop' ? 'stopping' :
        variables.action === 'restart' ? 'restarting' :
        variables.action === 'kill' ? 'stopping' : undefined
      
      // Update individual server query if it exists
      if (previousServer) {
        queryClient.setQueryData(serverKeys.detail(variables.uuid), (old: any) => ({
          ...old,
          status: optimisticStatus,
        }))
      }
      
      // Update server list query - use exact query key and create new array reference
      queryClient.setQueryData(serverKeys.lists(), (old: any) => {
        if (!old || !Array.isArray(old)) return old
        
        // Create a new array with the updated server
        return old.map((server: any) => 
          (server.uuid === variables.uuid || server.identifier === variables.uuid)
            ? { ...server, status: optimisticStatus }
            : server
        )
      })
      
      // Return context for rollback
      return { previousServer, previousList }
    },
    onError: (_err: any, variables: { uuid: string }, context: any) => {
      // Rollback on error
      if (context?.previousServer) {
        queryClient.setQueryData(serverKeys.detail(variables.uuid), context.previousServer)
      }
      if (context?.previousList) {
        queryClient.setQueryData(serverKeys.lists(), context.previousList)
      }
    },
    onSuccess: (_data: any, variables: { uuid: string; action: 'start' | 'stop' | 'restart' | 'kill' }) => {
      // Invalidate to refetch actual state from server
      queryClient.invalidateQueries({ queryKey: serverKeys.detail(variables.uuid) })
      queryClient.invalidateQueries({ queryKey: serverKeys.resources(variables.uuid) })
      queryClient.invalidateQueries({ queryKey: serverKeys.lists() })
    },
  })
}

// Mutation for setting primary allocation
export function usePrimaryAllocationMutation() {
  const queryClient = useQueryClient()
  
  return useMutation<any, Error, { uuid: string; allocationId: number }>({
    mutationFn: ({ uuid, allocationId }: { uuid: string; allocationId: number }) =>
      apiClient.setPrimaryAllocation(uuid, allocationId),
    onSuccess: (_data: any, variables: { uuid: string; allocationId: number }) => {
      // Invalidate server data to refresh allocations and address display
      queryClient.invalidateQueries({ queryKey: serverKeys.detail(variables.uuid) })
      // Also invalidate server list in case it's shown elsewhere
      queryClient.invalidateQueries({ queryKey: serverKeys.lists() })
    },
  })
}

// Hook for server search
export function useServerSearch(term: string, enabled = true) {
  return useQuery({
    queryKey: ['servers', 'search', term],
    queryFn: () => apiClient.searchServers(term),
    enabled: enabled && term.length >= 3,
    staleTime: 30_000,
  })
}

// Mutation for renaming server
export function useServerRenameMutation() {
  const queryClient = useQueryClient()
  
  return useMutation<any, Error, { uuid: string; name: string; description: string }>({
    mutationFn: ({ uuid, name, description }: { uuid: string; name: string; description: string }) =>
      apiClient.renameServer(uuid, name, description),
    onSuccess: (_data: any, variables: { uuid: string; name: string; description: string }) => {
      // Invalidate and refetch server data to show updated name
      queryClient.invalidateQueries({ queryKey: serverKeys.detail(variables.uuid) })
      queryClient.invalidateQueries({ queryKey: serverKeys.lists() })
    },
  })
}
